// Lobby HUD (§9.3, §14.2) over the live Lobby Playground: room code chip + COPY LINK, the player
// list (glyph, name, ready, host crown, bot badge, ping bars, ✕ kick for the host), bot fill,
// status/countdown, the round-1 vote strip with live counts, READY, and the host's SETTINGS and
// START. In-world screen: the bean keeps moving; Enter / (A) with nothing focused toggles READY,
// Tab or the D-pad brings focus into the panel and Esc / (B) gives it back to the bean.
import { RoomPhase, type Personality } from '@shared/ids';
import type { PlayerDTO, RoomStateDTO } from '@shared/protocol/messages';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { icon, identityBadge, MODE_ICONS } from '../icons';
import { DIFFICULTY_NAMES, MODE_NAMES, PERSONALITY_BLURBS, PERSONALITY_NAMES, mapName, roomLink } from '../labels';
import { mapThumb } from '../mapThumbs';
import { copyText, el, replayClass, setAttr, setHidden, setStyle, setText, toggleClass } from '../ui';
import { button, pingBars, setLabel } from '../widgets';
import { createPromptView } from '../hud/PromptView';

/** The vote cards with live counts; shared by the lobby strip and the intermission tally. */
export function voteStrip(deps: UiDeps, cls: string): { el: HTMLElement; render(dto: RoomStateDTO): void; cards(): HTMLButtonElement[] } {
  const strip = el('div', { class: cls, attrs: { role: 'group', 'aria-label': 'Vote for the next round' } });
  let cards: HTMLButtonElement[] = [];
  let key = '';
  const counts: HTMLElement[] = [];
  const pips: HTMLElement[] = [];
  return {
    el: strip,
    cards: () => cards,
    render(dto) {
      const k = dto.cards.map((c) => `${c.mode}:${c.map}`).join('|');
      if (k !== key) {
        key = k;
        counts.length = 0;
        pips.length = 0;
        cards = dto.cards.map((card, i) => {
          const count = el('span', { class: 'vc-count' });
          const pip = el('span', { class: 'vc-pips' });
          counts.push(count);
          pips.push(pip);
          const b = el('button', {
            class: 'vote-card',
            attrs: { type: 'button', 'aria-label': `Vote ${i + 1}: ${MODE_NAMES[card.mode]} on ${mapName(card.map)}`, 'data-keepfocus': true },
            on: { click: () => vote(deps, i) },
          },
            el('span', { class: 'vc-art' }, mapThumb(card.map), el('span', { class: 'vc-mode' }, icon(MODE_ICONS[card.mode]))),
            el('span', { class: 'vc-text' },
              el('span', { class: 'vc-modename', text: MODE_NAMES[card.mode] }),
              el('span', { class: 'vc-mapname', text: mapName(card.map) })),
            el('span', { class: 'vc-num key', text: String(i + 1) }),
            el('span', { class: 'vc-foot' }, pip, count),
            el('span', { class: 'vc-stamp' }, icon('checkBadge')));
          b.style.animationDelay = `${i * 70}ms`;
          return b;
        });
        strip.replaceChildren(...cards);
      }
      const total = Math.max(1, dto.votes.reduce((a, b) => a + b, 0));
      cards.forEach((b, i) => {
        const v = dto.votes[i] ?? 0;
        setText(counts[i], v === 1 ? '1 vote' : `${v} votes`);
        if (pips[i].childElementCount !== v) {
          pips[i].replaceChildren(...Array.from({ length: Math.min(8, v) }, () => el('i')));
          if (v > 0) replayClass(counts[i], 'bump');
        }
        setStyle(b, '--share', String(v / total));
        toggleClass(b, 'mine', deps.room.myVote === i);
        const leading = v > 0 && v === Math.max(...dto.votes);
        toggleClass(b, 'leading', leading);
      });
    },
  };
}

export function vote(deps: UiDeps, card: number): void {
  if (card !== 0 && card !== 1 && card !== 2) return;
  if (deps.room.spectator) return;
  deps.room.myVote = card;
  deps.services.playUi('click');
  deps.actions.send({ t: 'vote', card });
}

interface Row { root: HTMLElement; badge: HTMLElement; badgeKey: string; name: HTMLElement; tags: HTMLElement; ping: HTMLElement; ready: HTMLElement; kick: HTMLButtonElement }

export function create(deps: UiDeps): LiveScreen {
  const room = deps.room;
  const tiles = el('span', { class: 'code-tiles', attrs: { 'aria-label': 'Room code' } });
  const kind = el('span', { class: 'chip ink code-kind' });
  // Teams (§4.5, private rooms): the split is made at the start, so the lobby names the setting.
  const teamText = el('span');
  const teamChip = el('span', { class: 'chip team-chip' }, el('i', { class: 'tdot red' }), el('i', { class: 'tdot blue' }), teamText);
  teamChip.hidden = true;
  const copy = button({ label: 'COPY LINK', icon: 'link', variant: 'cream', size: 'small', onClick: async () => {
    const ok = await copyText(roomLink(room.code));
    setLabel(copy, ok ? 'COPIED!' : 'COPY FAILED');
    deps.services.playUi(ok ? 'coin' : 'error');
    setTimeout(() => setLabel(copy, 'COPY LINK'), 2000);
  } });
  const share = button({ icon: 'share', title: 'Share room link', variant: 'cream', size: 'small', onClick: () => {
    void navigator.share?.({ title: 'SHOVE OFF!', text: `Join my SHOVE OFF! room ${room.code}`, url: roomLink(room.code) }).catch(() => undefined);
  } });
  setHidden(share, typeof navigator.share !== 'function');

  const count = el('span', { class: 'pl-count' });
  const list = el('div', { class: 'pl-list' });
  const botInfo = el('div', { class: 'pl-bots' });
  // On touch the list folds to its header so it does not cover the move-stick zone.
  const plToggle = button({ icon: 'arrowUp', title: 'Show or hide the players', variant: 'cream', size: 'tiny', cls: 'round pl-toggle', onClick: () => {
    players.classList.toggle('collapsed');
  } });
  const players = el('div', { class: 'panel players collapsed', attrs: { 'aria-label': 'Players' } },
    el('div', { class: 'pl-head' }, el('span', { class: 'pl-title', text: 'BEANS' }), el('span', { class: 'row' }, count, plToggle)), list, botInfo);

  const statusText = el('span', { class: 'st-text' });
  const statusNum = el('span', { class: 'st-num' });
  const ring = el('span', { class: 'st-ring' }, statusNum);
  const status = el('div', { class: 'lobby-status', attrs: { 'aria-live': 'polite' } }, ring, statusText);

  const votes = voteStrip(deps, 'vote-strip');
  const voteWrap = el('div', { class: 'lobby-votes' }, el('div', { class: 'vs-title' }, icon('star'), el('span', { text: 'VOTE FOR ROUND 1' })), votes.el);

  const ready = button({ label: 'READY', icon: 'check', variant: 'pink', size: 'big', cls: 'ready-btn', onClick: () => toggleReady() });
  const readyHint = el('span', { class: 'ready-hint' });
  const start = button({ label: 'START', icon: 'flag', variant: 'mint', onClick: () => deps.actions.send({ t: 'start' }) });
  const settingsBtn = button({ icon: 'sliders', title: 'Room settings', onClick: () => deps.nav.show('createRoom', { edit: true }) });
  const menu = button({ icon: 'pause', title: 'Menu', variant: 'cream', cls: 'lobby-menu', onClick: () => deps.nav.show('pause') });
  const tabHint = el('div', { class: 'lobby-tabhint' });

  const rows = new Map<number, Row>();

  function toggleReady(): void {
    if (room.spectator) return;
    const now = !(room.me?.ready ?? false);
    deps.services.playUi(now ? 'coin' : 'back');
    deps.actions.send({ t: 'ready', ready: now });
    replayClass(ready, 'pressed');
  }

  function rowFor(p: PlayerDTO): Row {
    let r = rows.get(p.slot);
    if (!r) {
      const name = el('span', { class: 'name ellipsis' });
      const tags = el('span', { class: 'tags' });
      const ping = el('span', { class: 'pingwrap' });
      const readyMark = el('span', { class: 'ready-mark' }, icon('checkBadge'));
      const slot = p.slot;
      const kick = button({ icon: 'x', title: 'Kick', variant: 'danger', size: 'tiny', cls: 'round kick', onClick: () => deps.actions.send({ t: 'kick', slot }) });
      const badge = el('span');
      const root = el('div', { class: 'player-row' }, badge, name, tags, ping, readyMark, kick);
      r = { root, badge, badgeKey: '', name, tags, ping, ready: readyMark, kick };
      rows.set(p.slot, r);
    }
    return r;
  }

  function renderPlayers(dto: RoomStateDTO): void {
    const colorblind = deps.settings().accessibility.colorblind;
    const present = dto.players.filter((p): p is PlayerDTO => p !== null);
    const humans = present.filter((p) => !p.isBot).length;
    setText(count, `${present.length}/8`);
    const order: HTMLElement[] = [];
    for (const p of present) {
      const r = rowFor(p);
      const bk = `${p.identity}:${colorblind}`;
      if (bk !== r.badgeKey) {
        r.badgeKey = bk;
        const b = identityBadge(p.identity, colorblind);
        r.badge.replaceWith(b);
        r.badge = b;
      }
      setText(r.name, p.name);
      const tagNodes: HTMLElement[] = [];
      if (p.isHost) tagNodes.push(el('span', { class: 'tag host', attrs: { title: 'Host' } }, icon('crown')));
      if (p.isBot) {
        const pers = p.personality === -1 ? null : (p.personality as Personality);
        tagNodes.push(el('span', { class: 'tag bot', attrs: { title: pers === null ? `Bot (${DIFFICULTY_NAMES[p.difficulty]})` : `${PERSONALITY_NAMES[pers]} — ${PERSONALITY_BLURBS[pers]} (${DIFFICULTY_NAMES[p.difficulty]})` } }, icon('bot'), el('span', { text: 'BOT' })));
      }
      if (!p.isBot && !p.connected) tagNodes.push(el('span', { class: 'tag warn', attrs: { title: 'Reconnecting…' } }, icon('wifi'), el('span', { text: '…' })));
      else if (!p.isBot && p.afk) tagNodes.push(el('span', { class: 'tag warn', text: 'AFK' }));
      const tagKey = tagNodes.map((t) => t.outerHTML).join('');
      if (r.tags.dataset.k !== tagKey) {
        r.tags.dataset.k = tagKey;
        r.tags.replaceChildren(...tagNodes);
      }
      const pk = p.isBot ? 'bot' : String(p.ping > 180 ? 3 : p.ping > 120 ? 2 : 1);
      if (r.ping.dataset.k !== pk) {
        r.ping.dataset.k = pk;
        r.ping.replaceChildren(...(p.isBot ? [] : [pingBars(p.ping)]));
      }
      const wasReady = r.root.classList.contains('is-ready');
      toggleClass(r.root, 'is-ready', p.ready);
      if (p.ready && !wasReady) replayClass(r.ready, 'stamp-in');
      toggleClass(r.root, 'me', p.slot === room.slot);
      toggleClass(r.root, 'bot', p.isBot);
      toggleClass(r.root, 'away', !p.isBot && !p.connected);
      setHidden(r.kick, !(room.isHost && dto.kind === 'private' && p.slot !== room.slot && !p.isBot));
      order.push(r.root);
    }
    for (const [slot, r] of rows) if (dto.players[slot] === null) {
      r.root.remove();
      rows.delete(slot);
    }
    // Re-append in slot order only when the order changed (keeps focus on kick buttons).
    const cur = [...list.children];
    if (cur.length !== order.length || cur.some((n, i) => n !== order[i])) list.replaceChildren(...order);
    const s = dto.settings;
    const botCount = present.length - humans;
    setText(botInfo, `${humans} human${humans === 1 ? '' : 's'} • ${botCount} bot${botCount === 1 ? '' : 's'} (${s.botFill || dto.kind === 'quick' ? 'fill to 8' : `fixed ${s.botCount}`}, ${DIFFICULTY_NAMES[s.botDifficulty]})${dto.spectators.length > 0 ? ` • ${dto.spectators.length} watching` : ''}`);
  }

  function render(): void {
    const dto = room.dto;
    if (!dto || dto.phase !== RoomPhase.LOBBY) return;
    if (tiles.dataset.code !== dto.code) {
      tiles.dataset.code = dto.code;
      tiles.replaceChildren(...[...dto.code].map((c, i) => el('span', { class: 'code-tile static', text: c, style: { 'animation-delay': `${i * 60}ms` } })));
    }
    setText(kind, dto.kind === 'quick' ? 'QUICK PLAY' : 'PRIVATE ROOM');
    const teams = dto.kind === 'private' ? dto.settings.teams : 'off';
    setHidden(teamChip, teams === 'off');
    if (teams !== 'off') {
      setText(teamText, teams === 'split' ? 'RED vs BLUE' : 'HUMANS vs BOTS');
      setAttr(teamChip, 'title', teams === 'split' ? 'Teams: everyone is split into Red and Blue when the match starts' : 'Teams: all humans on Blue against the bots on Red');
    }
    renderPlayers(dto);
    const me = room.me;
    setLabel(ready, me?.ready ? 'READY!' : 'READY');
    toggleClass(ready, 'on', me?.ready ?? false);
    setHidden(ready, room.spectator);
    const host = room.isHost && dto.kind === 'private';
    setHidden(start, !host);
    setHidden(settingsBtn, !host);
    votes.render(dto);
  }
  room.onChange(() => {
    if (!root.hidden) render();
  });

  function renderHints(): void {
    const dev = deps.services.input.device;
    readyHint.replaceChildren(...(dev === 'touch' ? [] : [actionCap(deps, 'ready')]));
    tabHint.replaceChildren(...(dev === 'touch' ? [] : [actionCap(deps, 'scoreboard'), el('span', { text: 'lobby menu & votes' })]));
  }
  deps.services.input.onChange(() => {
    if (!root.hidden) renderHints();
  });
  const prompt = createPromptView(deps, 'lobby-prompt');

  const codeChip = el('div', { class: 'panel code-chip' },
    el('div', { class: 'cc-top' }, el('span', { class: 'cc-label', text: 'ROOM CODE' }), el('span', { class: 'cc-chips' }, teamChip, kind)),
    el('div', { class: 'row cc-row' }, tiles, copy, share));
  const left = el('div', { class: 'lobby-left' }, codeChip, players);
  const root = el('div', { class: 'screen lobby', attrs: { 'data-nofocus': true } },
    left, status, voteWrap, prompt.el,
    el('div', { class: 'lobby-actions' }, el('div', { class: 'la-row' }, menu, settingsBtn, start), el('div', { class: 'ready-wrap' }, ready, readyHint)),
    tabHint);

  return {
    el: root,
    onShow() {
      setLabel(copy, 'COPY LINK');
      render();
      renderHints();
    },
    onNav(action: NavAction) {
      const focused = document.activeElement instanceof HTMLElement && root.contains(document.activeElement);
      if (action === 'confirm' && !focused) {
        toggleReady();
        return true;
      }
      if (action === 'scoreboard') {
        (focused ? (document.activeElement as HTMLElement) : ready).focus();
        return true;
      }
      return false;
    },
    update(view) {
      prompt.update(view);
      // Narrow (portrait phone): the status joins the left column under the code chip, so it
      // never sits on the BEANS header; elsewhere it is the top-center pill (§9.3).
      const narrow = root.closest('#ui')?.classList.contains('narrow') ?? false;
      if (narrow !== (status.parentElement === left)) {
        if (narrow) left.insertBefore(status, players);
        else root.insertBefore(status, voteWrap);
      }
      const dto = room.dto;
      if (!dto) return;
      const humans = room.humans();
      const counting = dto.lobbyCountdownEndTick >= 0;
      const secs = counting ? Math.ceil(room.secondsUntil(dto.lobbyCountdownEndTick)) : 0;
      setHidden(ring, !counting);
      if (counting) {
        if (statusNum.textContent !== String(secs)) {
          setText(statusNum, String(secs));
          replayClass(ring, 'tick');
          if (secs <= 3) deps.services.playUi('hover');
        }
        setAttr(status, 'data-urgent', String(secs <= 3));
      }
      const allReady = dto.players.every((p) => p === null || p.isBot || p.ready);
      const text = counting ? (allReady && humans > 0 ? 'Everyone ready — here we go!' : 'Match starting')
        : dto.kind === 'private' && room.isHost ? 'Press START when everyone is here'
          : dto.kind === 'private' ? 'Waiting for the host…'
            : humans > 1 ? 'Ready up!' : 'Waiting for players…';
      setText(statusText, text);
    },
  };
}
